import { useState } from 'react'
import PalletIdText from '@/components/PalletIdText'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Snowflake } from 'lucide-react'
import DateInput from '@/components/DateInput'
import Modal from '@/components/Modal'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { ValidationError } from '@/lib/errorReporter'
import { withTimeout } from '@/lib/saveTimeout'
import { sastTodayYMD } from '@/lib/dates'

// ---------------------------------------------------------------------------
// BackdatePlacementModal — correct a pallet's coldroom placement date
// (pallets.stocked_at). This is the date the aging clock runs off (see
// effectiveAgeStart). Cold-storage billing does NOT: since mig 20261033200000 /
// 20261035100000 it counts the SAST days the pallet stood IN_COLDROOM in a room,
// from pallets_audit, so moving this date changes no bill (F07-FLOOR:WORDS).
// Placement is stamped automatically when the pallet enters the coldroom, but
// paper-record catch-up and forgotten-scan cases need a manual back-date.
//
// A plain UPDATE of stocked_at is SAFE: the state-machine trigger early-returns
// when status is unchanged (mig 408), so it won't re-stamp NOW() over our value.
// The reason lands in pallets_audit via update_reason (mig 373).
//
// Reused by the Coldroom cell drawer and Pallet Detail. The CALLER gates who
// sees the trigger button (permission check) — this modal is presentation-only
// once opened.
// ---------------------------------------------------------------------------
// fallow-ignore-next-line complexity — modal shell: form state + validation + render.
export default function BackdatePlacementModal({
  palletId, stockedAt, packedAt, version, onClose, onSuccess,
}: {
  palletId:  string
  /** Current placement date (ISO) — may be null if never stocked. */
  stockedAt: string | null
  /** Pack date (ISO) — placement can't precede it. Lower bound for the picker. */
  packedAt:  string
  version:   number
  onClose:   () => void
  onSuccess?: () => void
}) {
  const qc = useQueryClient()
  const [date,   setDate]   = useState(stockedAt?.slice(0, 10) ?? sastTodayYMD())
  const [reason, setReason] = useState('')
  const [error,  setError]  = useState<string | null>(null)

  const currentDate = stockedAt?.slice(0, 10) ?? null
  const packedDate  = packedAt?.slice(0, 10) ?? undefined
  const dateChanged = date !== '' && date !== currentDate

  const save = useMutation({
    // fallow-ignore-next-line complexity — sequential soft-validation guards.
    mutationFn: async () => {
      if (!date) throw new ValidationError('Pick a placement date.')
      if (!dateChanged) throw new ValidationError('Pick a different date — this is already the recorded placement date.')
      if (packedDate && date < packedDate) {
        throw new ValidationError(`Placement can't be before the pack date (${packedDate}).`)
      }
      if (date > sastTodayYMD()) throw new ValidationError("Placement can't be in the future.")
      if (!reason.trim()) throw new ValidationError('Reason is required — it lands in the audit trail.')

      // Noon SAST so the pallet lands on the chosen calendar day unambiguously,
      // matching the packed_at back-date convention (PalletDetail edit dialog).
      const iso = `${date}T12:00:00+02:00`
      try {
        await withTimeout(
          updateWithVersion('pallets',
            { col: 'pallet_id', value: palletId },
            version,
            { stocked_at: iso, update_reason: reason.trim() },
          ),
          'Coldroom: back-date placement',
        )
      } catch (e) {
        if (isStaleWriteError(e)) {
          throw new ValidationError('This pallet was just changed by someone else. Refresh and try again — the date was not changed.')
        }
        throw e
      }
    },
    onSuccess: () => {
      // Broad invalidation so every aging/cold surface re-reads. Prefix-matched.
      qc.invalidateQueries({ queryKey: ['coldroom-grid'] })
      qc.invalidateQueries({ queryKey: ['coldroom-counts'] })
      qc.invalidateQueries({ queryKey: ['cold-storage-accrued'] })
      qc.invalidateQueries({ queryKey: ['coldroom-report'] })
      qc.invalidateQueries({ queryKey: ['pallet-cold-storage', palletId] })
      qc.invalidateQueries({ queryKey: ['pallet-detail', palletId] })
      qc.invalidateQueries({ queryKey: ['pallets'] })
      onSuccess?.()
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  // Dirty baseline (K-forms #5): every editable field, synchronous at mount, so
  // a backdrop tap can't silently discard a typed reason.
  const snapshot = `${date}|${reason}`

  return (
    <Modal
      onClose={onClose}
      snapshot={snapshot}
      onSubmit={() => { if (dateChanged && reason.trim()) save.mutate() }}
      isPending={save.isPending}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          <Snowflake size={16} className="text-cyan-400 shrink-0" />
          <span className="truncate">Adjust placement date</span>
        </span>
      }
      footer={attemptClose => (
        <footer className="px-4 py-3 border-t hairline flex items-center justify-end gap-2 shrink-0">
          <button type="button" onClick={attemptClose} className="btn-ghost">Cancel</button>
          <button
            type="submit"
            disabled={save.isPending || !dateChanged || !reason.trim()}
            className="btn-primary">
            {save.isPending ? 'Saving…' : 'Save placement date'}
          </button>
        </footer>
      )}>
      <div className="space-y-3">
        <p className="text-xs text-fg-muted">
          Pallet <PalletIdText id={palletId} className="text-fg-secondary" />
        </p>
        <p className="text-[11px] text-fg-muted -mt-1.5">
          The aging clock runs off this date. Back-date it to when the fruit actually went
          into cold, not when the run started. Cold-storage billing does not move with it: it
          counts the days the pallet actually stood in a coldroom, from its status history.
        </p>

        <div>
          <label className="text-xs text-fg-muted block mb-1">Placed in coldroom on</label>
          <DateInput
            label="Placed in coldroom on"
            min={packedDate}
            max={sastTodayYMD()}
            value={date}
            onChange={setDate}
            className="w-full surface text-fg text-sm rounded-lg p-2 border hairline-strong" />
        </div>

        <div>
          <label className="text-xs text-fg-muted block mb-1">
            Reason <span className="text-red-400">*</span>
          <input type="text" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. scanned in late, paper record catch-up"
            className="mt-1 w-full surface text-fg text-sm rounded-lg p-2 border hairline-strong" />
          </label>
          <p className="text-[10px] text-fg-muted mt-0.5">Lands in the pallet audit trail.</p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-700 bg-red-900/40 p-2.5 text-xs text-red-200 whitespace-pre-line">
            {error}
          </div>
        )}
      </div>
    </Modal>
  )
}
